import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const W = 260, H = 380, LANES = 4, CARW = 40, CARH = 60;
const laneX = (i) => 20 + i * ((W - 40) / LANES) + ((W - 40) / LANES - CARW) / 2;

export default function RoadRacer() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const move = useRef(0);
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");

  const start = () => { g.current = { lane: 1, x: laneX(1), enemies: [], spawn: 0, speed: 4, dash: 0, dist: 0 }; scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep(); };

  const shift = (d) => { const s = g.current; if (!s || status !== "playing") return; s.lane = Math.max(0, Math.min(LANES - 1, s.lane + d)); sfx.click(); };

  useEffect(() => {
    const kd = (e) => { if (["ArrowLeft", "a"].includes(e.key)) shift(-1); else if (["ArrowRight", "d"].includes(e.key)) shift(1); };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#2b2b33"; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#4b7a3a"; ctx.fillRect(0, 0, 16, H); ctx.fillRect(W - 16, 0, 16, H);
      const s = g.current;
      ctx.fillStyle = "#e8e8e8"; for (let i = 1; i < LANES; i++) { const x = 20 + i * ((W - 40) / LANES); for (let y = ((s.dash || 0) % 40) - 40; y < H; y += 40) ctx.fillRect(x - 2, y, 4, 20); }
      ctx.fillStyle = "#22d3ee"; ctx.fillRect(s.x || laneX(1), H - CARH - 14, CARW, CARH);
      (s.enemies || []).forEach((e) => { ctx.fillStyle = e.c; ctx.fillRect(e.x, e.y, CARW, CARH); });
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      s.dash += s.speed; s.dist += s.speed;
      const targetX = laneX(s.lane); s.x += (targetX - s.x) * 0.3;
      s.spawn--;
      if (s.spawn <= 0) { s.spawn = 40 + (Math.random() * 30 | 0); const lane = (Math.random() * LANES) | 0; s.enemies.push({ x: laneX(lane), y: -CARH, c: ["#ef4444", "#e8b04b", "#a78bfa"][(Math.random() * 3) | 0] }); }
      const py = H - CARH - 14;
      for (const e of s.enemies) e.y += s.speed;
      for (const e of s.enemies) { if (e.y + CARH > py && e.y < py + CARH && Math.abs(e.x - s.x) < CARW - 6) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; } }
      s.enemies = s.enemies.filter((e) => { if (e.y > H) { scoreRef.current += 5; setScore(scoreRef.current); return false; } return true; });
      s.speed = 4 + scoreRef.current / 120;
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2"><AScore label="Score" value={score} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[240px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-2">
            {status === "over" && <AResult win={false} text={`Crash! · ${score}`} testId="racer-over" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-kirana-cream/70">Dodge the traffic!</p>
      <div className="mt-1 flex gap-4">
        <PadButton testId="racer-left" onPress={() => shift(-1)}><ArrowLeft className="h-5 w-5" /></PadButton>
        <PadButton testId="racer-right" onPress={() => shift(1)}><ArrowRight className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
